// InstanceWebStoreDesign.js
import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Alert,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import ImageIcon from "@mui/icons-material/Image";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import apiClient from "../../../../../services/apiClient";
import { normalizePromoStorePaymentGatewayState } from "../promoStorePaymentGatewayState";
import { buildPromoStoreBrandingSyncPayload } from "./promoStoreBrandingSync";
import PromoStoreAddonConnection from "./PromoStoreAddonConnection";
import { fetchPromoStoreAddonRegistry } from "../../../../../services/promoStoreAddons";
import {
  PROMO_STORE_MENU_BUILT_INS,
  createPromoStoreMenuItemId,
  ensurePromoStoreMenuConfig,
  sanitizePromoStoreMenuUrl,
  setPromoStoreMenuConfig,
} from "./promoStoreMenuConfig";

// -------------------------
// Base Templates (existing)
// -------------------------
const TEMPLATE_PRESETS = [
  {
    id: "classic-elite",
    name: "Classic Elite",
    label: "Classic",
    description: "Clean header, white background, strong accent color.",
  },
  {
    id: "custom-elite",
    name: "Custom Elite",
    label: "Custom",
    description: "Flexible layout for deeper company/program builds.",
  },
];

// Map of template → thumbnail image URL
const TEMPLATE_THUMBNAILS = {
  "classic-elite":
    "https://pub-21a541dd56844462b17b8392190c34da.r2.dev/customer-files/dd1a4ef9-dc15-438b-8195-bd656575e9b0.png",
  "custom-elite":
    "https://pub-21a541dd56844462b17b8392190c34da.r2.dev/customer-files/17ff308d-bda1-4ea2-8d7e-d686742f73c1.png",
};

const resolveAttachedCustomerId = (store) => {
  if (Array.isArray(store?.attachedCustomers) && store.attachedCustomers[0]) return store.attachedCustomers[0];
  if (Array.isArray(store?.AttachedCustomers) && store.AttachedCustomers[0]) return store.AttachedCustomers[0];
  if (Array.isArray(store?.AttachedCustomerIds) && store.AttachedCustomerIds[0]) return store.AttachedCustomerIds[0];

  return (
    store?.AttachedCustomerId ||
    store?.attachedCustomerId ||
    store?.customerId ||
    store?.CustomerId ||
    store?.customer?.id ||
    store?.Customer?.id ||
    null
  );
};

const resolveCustomerFileUrl = (file = {}) =>
  file.displayUrl ||
  file.previewUrl ||
  file.PreviewUrl ||
  file.thumbnailUrl ||
  file.downloadUrl ||
  file.filePath ||
  "";

// ----------------------------------------------------
// ✅ Add-ons
// Keys MUST match what we store in TemplateConfig.addons
// ----------------------------------------------------

// -------------------------
// Pages config (BB-style)
// -------------------------
const PAGES_CONFIG = [
  { name: "home", icon: "fa-home", label: "Home" },
  { name: "services", icon: "fa-cogs", label: "Services" },
  { name: "about", icon: "fa-info-circle", label: "About" },
  { name: "team", icon: "fa-users", label: "Team" },
  { name: "shop", icon: "fa-shopping-cart", label: "Shop" },
  { name: "events", icon: "fa-calendar", label: "Events" },
  { name: "menu", icon: "fa-utensils", label: "Menu" },
  { name: "contact", icon: "fa-envelope", label: "Contact" },
];

const PROMOSTORE_SUBTYPES = [
  { value: "company-store", label: "Company Store (staff merch)" },
  { value: "pop-up", label: "Pop-up Store (limited-time drop)" },
  { value: "program", label: "Program Store (dealer/partner program)" },
  { value: "rewards", label: "Rewards Store (points/incentives)" },
  { value: "event", label: "Event Store (conference/fundraiser)" },
  { value: "catalog", label: "Catalog Store (always-on merch)" },
  { value: "voucher", label: "Voucher Store (credits/budget)" },
  { value: "dropship", label: "Dropship Store (multi-location shipping)" },
];

const storeTypeLabel = (type) => {
  switch (type) {
    case "company-store":
      return "Company Store";
    case "pop-up-store":
      return "Pop-up Store";
    case "program":
      return "Program Store";
    default:
      return "Store";
  }
};

// Small helper: safe nested read
const get = (obj, path, fallback = undefined) => {
  try {
    return path.split(".").reduce((acc, key) => acc?.[key], obj) ?? fallback;
  } catch {
    return fallback;
  }
};

const parseAxiosOrFetchError = (err) => {
  const resp = err?.response;
  if (resp?.data) return resp.data;
  if (err?.data) return err.data;
  return null;
};

const friendlyAiError = (payload) => {
  if (!payload)
    return {
      title: "Something went wrong",
      body: "No additional details were returned.",
    };

  const { key, message, requestId, stage, details } = payload;

  let title = message || "AI generation failed";
  let body = "";

  if (key === "validation_error") body = "Please adjust your prompt/pages and try again.";
  if (key === "timeout")
    body = "The generator didn’t respond in time. Try again, or reduce selected pages.";
  if (key === "network_error")
    body = "We couldn’t reach the generator service. This is usually a server/network issue.";
  if (key === "businessbloc_error") {
    body = `The generator returned an error (status: ${details?.bbStatus || "unknown"}).`;
  }
  if (key === "server_error")
    body = "Backend error. Please try again and share the Request ID with the dev team.";

  const footerBits = [requestId ? `Request ID: ${requestId}` : null, stage ? `Stage: ${stage}` : null].filter(
    Boolean
  );

  return {
    title,
    body,
    meta: footerBits.join(" • "),
    raw: key === "businessbloc_error" ? details?.bb : null,
  };
};

// --------------------------------------------
// ✅ Add-ons state helpers
// --------------------------------------------
const ensureAddonsShape = (templateConfig) => {
  const cfg = templateConfig || {};
  const addons = cfg.addons || {};
  return {
    ...cfg,
    addons: {
      active: { ...(addons.active || {}) },
      settings: { ...(addons.settings || {}) },
    },
  };
};

export const isPromoStoreAddonActivatable = (addon = {}) =>
  addon?.activatable !== false && addon?.status !== "coming-soon";

export const filterActivatablePromoStoreAddons = (addons = []) =>
  Array.isArray(addons) ? addons.filter(isPromoStoreAddonActivatable) : [];

// ✅ Ensure all keys exist (so UI always shows stable defaults)
const ensureAllAddonKeys = (templateConfig, addonDefinitions = []) => {
  const cfg = ensureAddonsShape(templateConfig);
  const active = cfg.addons.active;

  addonDefinitions.forEach((a) => {
    if (typeof active[a.key] === "undefined") {
      active[a.key] = false;
    }
  });

  return cfg;
};

// ---------- Default settings ----------
const DEFAULT_ADDON_SETTINGS = {
  "user-addresses": {
    allowMultiple: true,
    locationSets: {},
    checkoutSelectorEnabled: true,
    checkoutLockingEnabled: true,
  },
  "payment-gateway": {
    mode: "request_quote",
    requestQuoteLabel: "Request Quote",
    quoteGatewayTitle: "Request Quote/Estimate",
    quoteGatewayDescription: "Request a quote for your order.",
    quoteCheckoutButtonText: "Submit Request",
    quoteEmailText: "Thank you for requesting a quote. We will get back to you shortly.",
    quoteEmailAdminNotice: "This order is a quote request.",
  },
  "product-decoration": {
    deriveFromUserAddresses: false,
  },
  "branded-gateway": {
    homepageLocked: true,
    allowGuestBrowse: false,
  },
  "email-domain-guard": {
    domains: [],
    failClosed: true,
  },
};

const ensureAddonDefaults = (templateConfig, addonDefinitions = []) => {
  const cfg = ensureAllAddonKeys(
    normalizePromoStorePaymentGatewayState(templateConfig || {}),
    addonDefinitions,
  );
  cfg.addons.settings = cfg.addons.settings || {};

  addonDefinitions.forEach((a) => {
    cfg.addons.settings[a.key] = {
      ...(DEFAULT_ADDON_SETTINGS[a.key] || {}),
      ...(cfg.addons.settings[a.key] || {}),
    };
  });

  return cfg;
};

const getAddonSettings = (templateConfig, addonKey, addonDefinitions = []) => {
  const cfg = ensureAddonDefaults(templateConfig, addonDefinitions);
  return cfg.addons.settings?.[addonKey] || {};
};

const setAddonSettings = (templateConfig, addonKey, partial, addonDefinitions = []) => {
  const cfg = ensureAddonDefaults(templateConfig, addonDefinitions);
  const prev = cfg.addons.settings?.[addonKey] || {};
  cfg.addons.settings[addonKey] = { ...prev, ...partial };
  return normalizePromoStorePaymentGatewayState(cfg);
};

// --------------------------------------------
// ✅ Map UI settings -> WP REST payload
// --------------------------------------------
const buildWpAddonSettingsPayload = (templateConfig) => {
  const cfg = ensureAddonsShape(
    normalizePromoStorePaymentGatewayState(templateConfig || {}),
  );
  const active = cfg.addons.active || {};
  const settings = cfg.addons.settings || {};

  const wpActive = {};
  Object.keys(active).forEach((k) => {
    wpActive[k] = !!active[k];
  });

  const wpSettings = {};

  // Payment Gateway
  const pg = settings["payment-gateway"] || {};
  wpSettings["payment-gateway"] = {
    mode: pg.mode || "request_quote",
    requestQuoteLabel: pg.requestQuoteLabel || "Request Quote",
    quoteGatewayTitle: pg.quoteGatewayTitle || "",
    quoteGatewayDescription: pg.quoteGatewayDescription || "",
    quoteCheckoutButtonText: pg.quoteCheckoutButtonText || "",
    quoteEmailText: pg.quoteEmailText || "",
    quoteEmailAdminNotice: pg.quoteEmailAdminNotice || "",
  };

  // Email Domain Guard
  const dg = settings["email-domain-guard"] || {};
  wpSettings["email-domain-guard"] = {
    domains: Array.isArray(dg.domains) ? dg.domains : [],
  };

  // User Addresses (WP add-on settings)
  const ua = settings["user-addresses"] || {};
  wpSettings["user-addresses"] = {
    allowMultiple: !!ua.allowMultiple,
    checkoutSelectorEnabled: ua?.checkoutSelectorEnabled !== false,
    checkoutLockingEnabled: ua?.checkoutLockingEnabled !== false,
    locationSets:
      ua?.locationSets && typeof ua.locationSets === "object" && !Array.isArray(ua.locationSets)
        ? ua.locationSets
        : {},
  };

  const pd = settings["product-decoration"] || {};
  wpSettings["product-decoration"] = {
    deriveFromUserAddresses: !!pd.deriveFromUserAddresses,
  };

  const bg = settings["branded-gateway"] || {};
  wpSettings["branded-gateway"] = {
    homepageLocked: !!bg.homepageLocked,
    allowGuestBrowse: !!bg.allowGuestBrowse,
  };

  return { active: wpActive, settings: wpSettings };
};

// --------------------------------------------
// ✅ Location Set Editor (User Addresses)
// --------------------------------------------
const blankAddress = () => ({
  label: "",
  address1: "",
  address2: "",
  city: "",
  province: "",
  postalCode: "",
  country: "CA",
});

function LocationSetEditor({ siteKey, value, onChange }) {
  // value == locationSets object
  const locationSets = value || {};
  const thisSet = locationSets?.[siteKey] || { label: siteKey, addresses: {} };
  const addresses = thisSet.addresses || {};
  const [editableKeys, setEditableKeys] = useState({});
  const [keyDrafts, setKeyDrafts] = useState({});
  const [keyErrors, setKeyErrors] = useState({});

  const updateSet = (partial) => {
    const next = {
      ...locationSets,
      [siteKey]: {
        ...thisSet,
        ...partial,
        addresses: partial.addresses ? partial.addresses : thisSet.addresses,
      },
    };
    onChange(next);
  };

  const updateAddress = (key, partial) => {
    const nextAddresses = { ...addresses, [key]: { ...(addresses[key] || {}), ...partial } };
    updateSet({ addresses: nextAddresses });
  };

  const addAddress = () => {
    let i = 1;
    let key = `LOC${String(i).padStart(3, "0")}`;
    while (addresses[key]) {
      i += 1;
      key = `LOC${String(i).padStart(3, "0")}`;
    }
    updateAddress(key, blankAddress());
  };

  const removeAddress = (key) => {
    const next = { ...addresses };
    delete next[key];
    updateSet({ addresses: next });
  };

  const enableKeyEdit = (addressKey) => {
    setEditableKeys((prev) => ({ ...prev, [addressKey]: true }));
    setKeyDrafts((prev) => ({ ...prev, [addressKey]: addressKey }));
    setKeyErrors((prev) => ({ ...prev, [addressKey]: "" }));
  };

  const disableKeyEdit = (addressKey) => {
    setEditableKeys((prev) => ({ ...prev, [addressKey]: false }));
    setKeyDrafts((prev) => ({ ...prev, [addressKey]: addressKey }));
    setKeyErrors((prev) => ({ ...prev, [addressKey]: "" }));
  };

  const commitAddressKey = (addressKey) => {
    const draftValue = String(keyDrafts[addressKey] ?? addressKey).trim();

    if (!draftValue) {
      setKeyErrors((prev) => ({ ...prev, [addressKey]: "Key is required." }));
      return;
    }

    if (draftValue === addressKey) {
      disableKeyEdit(addressKey);
      return;
    }

    if (addresses[draftValue]) {
      setKeyErrors((prev) => ({ ...prev, [addressKey]: "That key already exists in this location set." }));
      return;
    }

    const nextAddresses = {};
    Object.entries(addresses).forEach(([existingKey, addressValue]) => {
      nextAddresses[existingKey === addressKey ? draftValue : existingKey] = addressValue;
    });

    updateSet({ addresses: nextAddresses });
    setEditableKeys((prev) => ({ ...prev, [addressKey]: false, [draftValue]: false }));
    setKeyDrafts((prev) => ({ ...prev, [addressKey]: draftValue, [draftValue]: draftValue }));
    setKeyErrors((prev) => ({ ...prev, [addressKey]: "", [draftValue]: "" }));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <TextField
        label="Location Set Label (optional)"
        value={thisSet.label || ""}
        onChange={(e) => updateSet({ label: e.target.value })}
        fullWidth
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontWeight: 800 }}>Locations</Typography>
        <Button
          size="small"
          variant="contained"
          onClick={addAddress}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            backgroundColor: "#c3171d",
            "&:hover": { backgroundColor: "#a11217" },
          }}
        >
          Add Location
        </Button>
      </Box>

      {Object.keys(addresses).length === 0 ? (
        <Typography variant="body2" sx={{ color: "#777" }}>
          No locations added yet.
        </Typography>
      ) : (
        Object.entries(addresses).map(([key, addr]) => (
          <Box
            key={key}
            sx={{
              border: "1px solid #eee",
              borderRadius: 2,
              p: 1.5,
              background: "#fff",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography sx={{ fontWeight: 800 }}>
                {addr?.label || "Unnamed"} <span style={{ color: "#999" }}>({key})</span>
              </Typography>
              <Button
                size="small"
                onClick={() => removeAddress(key)}
                sx={{ textTransform: "none", color: "#b00020", fontWeight: 800 }}
              >
                Remove
              </Button>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 1.25,
              }}
            >
              <TextField
                label="Key"
                value={editableKeys[key] ? (keyDrafts[key] ?? key) : key}
                onChange={(e) => {
                  setKeyDrafts((prev) => ({ ...prev, [key]: e.target.value }));
                  if (keyErrors[key]) {
                    setKeyErrors((prev) => ({ ...prev, [key]: "" }));
                  }
                }}
                onBlur={() => {
                  if (editableKeys[key]) {
                    commitAddressKey(key);
                  }
                }}
                onKeyDown={(e) => {
                  if (!editableKeys[key]) return;
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitAddressKey(key);
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    disableKeyEdit(key);
                  }
                }}
                disabled={!editableKeys[key]}
                error={!!keyErrors[key]}
                helperText={keyErrors[key] || "Used as the internal code (ex: CAN001)"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={editableKeys[key] ? "Save custom key" : "Override key manually"}>
                        <IconButton
                          edge="end"
                          size="small"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            if (editableKeys[key]) {
                              commitAddressKey(key);
                              return;
                            }
                            enableKeyEdit(key);
                          }}
                        >
                          {editableKeys[key] ? (
                            <VisibilityOffOutlinedIcon fontSize="small" />
                          ) : (
                            <VisibilityOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
              <TextField
                label="Label"
                value={addr?.label || ""}
                onChange={(e) => updateAddress(key, { label: e.target.value })}
                fullWidth
              />

              <TextField
                label="Address 1"
                value={addr?.address1 || ""}
                onChange={(e) => updateAddress(key, { address1: e.target.value })}
                fullWidth
              />
              <TextField
                label="Address 2"
                value={addr?.address2 || ""}
                onChange={(e) => updateAddress(key, { address2: e.target.value })}
                fullWidth
              />

              <TextField
                label="City"
                value={addr?.city || ""}
                onChange={(e) => updateAddress(key, { city: e.target.value })}
                fullWidth
              />
              <TextField
                label="Province/State"
                value={addr?.province || ""}
                onChange={(e) => updateAddress(key, { province: e.target.value })}
                fullWidth
              />

              <TextField
                label="Postal Code"
                value={addr?.postalCode || ""}
                onChange={(e) => updateAddress(key, { postalCode: e.target.value })}
                fullWidth
              />
              <TextField
                label="Country"
                value={addr?.country || "CA"}
                onChange={(e) => updateAddress(key, { country: e.target.value })}
                fullWidth
              />
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}

// --------------------------------------------
// ✅ NEW: Pages helpers (Custom Elite)
// --------------------------------------------
function buildWpBase({ domain, path }) {
  const p = path || "/";
  return `https://${domain}${p}`.replace(/\/+$/, "/");
}

function normalizeWpPage(p = {}) {
  const id = p?.id ?? p?.ID ?? p?.post_id ?? null;
  const title =
    p?.title?.rendered ??
    p?.title ??
    p?.post_title ??
    p?.name ??
    p?.slug ??
    "Untitled";
  const slug = p?.slug ?? p?.post_name ?? p?.name ?? "";
  const link = p?.link ?? p?.url ?? null;

  return {
    id: id != null ? Number(id) : null,
    title,
    slug: String(slug || ""),
    link: link ? String(link) : null,
    isFrontPage: !!(p?.isFrontPage || p?.front_page || p?.is_front_page),
    raw: p,
  };
}

function buildPageViewUrl(store, page) {
  const base = buildWpBase(store);
  const slug = page?.slug || "";

  if (page?.link) return page.link;
  if (!slug || slug === "home" || page?.isFrontPage) return base;

  return `${base}${slug}/`;
}

function buildPageEditUrl(store, page) {
  const base = buildWpBase(store);
  const id = page?.id;
  if (!id) return null;
  return `${base}wp-admin/post.php?post=${id}&action=elementor`;
}

function getBannerConfig(store = {}) {
  const cfg = store?.TemplateConfig && typeof store.TemplateConfig === "object" ? store.TemplateConfig : {};
  const banners = cfg.banners && typeof cfg.banners === "object" ? cfg.banners : {};

  return {
    mainBannerUrl: store?.HomepageBannerUrl || banners.mainBannerUrl || "",
    cartBannerUrl: banners.cartBannerUrl || "",
    myAccountBannerUrl: banners.myAccountBannerUrl || "",
    productPageBannerUrl: banners.productPageBannerUrl || "",
  };
}

function setBannerConfig(store = {}, key, value) {
  const cfg = store?.TemplateConfig && typeof store.TemplateConfig === "object" ? store.TemplateConfig : {};
  const current = getBannerConfig(store);
  return {
    ...cfg,
    banners: {
      ...current,
      [key]: value,
    },
  };
}

const SortableMenuItem = ({ item, category, onChange, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const original = item.type === "category"
    ? `Category: ${category?.name || "Missing category"}`
    : item.type === "custom-link" ? "Custom link" : `Store page: ${PROMO_STORE_MENU_BUILT_INS.find((entry) => entry.type === item.type)?.label || item.type}`;
  return (
    <Accordion ref={setNodeRef} disableGutters sx={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.65 : 1, border: "1px solid #e1e5ea", borderRadius: "8px !important", mb: 1, "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <IconButton size="small" {...attributes} {...listeners} aria-label={`Reorder ${item.label}`} onClick={(event) => event.stopPropagation()} sx={{ cursor: "grab", mr: 1 }}>
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700 }}>{item.label || (item.type === "category" ? "Missing category" : "Untitled item")}</Typography>
          <Typography variant="caption" color="text.secondary">{original}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0 }}>
        <TextField size="small" label="Navigation Label" value={item.label || ""} onChange={(event) => onChange({ label: event.target.value })} required />
        {item.type === "custom-link" && (
          <TextField
            size="small"
            label="URL"
            value={item.url || ""}
            onChange={(event) => onChange({ url: event.target.value })}
            onBlur={(event) => onChange({ url: sanitizePromoStoreMenuUrl(event.target.value) })}
            error={Boolean(item.url) && !sanitizePromoStoreMenuUrl(item.url)}
            helperText="Use https://, http://, mailto:, tel:, or a site-relative /path."
            required
          />
        )}
        <Button color="error" size="small" onClick={onRemove} sx={{ alignSelf: "flex-start", textTransform: "none" }}>Remove</Button>
      </AccordionDetails>
    </Accordion>
  );
};

const InstanceWebStoreDesign = ({
  store,
  categories = [],
  onStoreChange,
  onGenerateAiDesign,
  onInitiateSync,

  // ✅ NEW: hook to fetch pages when Custom Elite is enabled.
  // Recommended: parent calls Node -> WP (secret) and returns { pages: [...] }.
  onGetWpPages,
  onRefreshWpPages,
}) => {
  const selectedTemplateId = store?.TemplateConfig?.id || null;
  const isCustomElite = selectedTemplateId === "custom-elite";
  const [designTab, setDesignTab] = useState("branding");
  const [addonDefinitions, setAddonDefinitions] = useState([]);
  const [addonCatalogError, setAddonCatalogError] = useState("");
  const configurableAddonDefinitions = useMemo(
    () => filterActivatablePromoStoreAddons(addonDefinitions),
    [addonDefinitions]
  );

  useEffect(() => {
    let active = true;
    fetchPromoStoreAddonRegistry()
      .then((catalog) => {
        if (active) setAddonDefinitions(Array.isArray(catalog?.data) ? catalog.data : []);
      })
      .catch(() => {
        if (active) setAddonCatalogError("Unable to load PromoStore add-ons.");
      });
    return () => { active = false; };
  }, []);

  const logoUrl = store?.ShopLogoUrl || store?.shopLogoUrl || "";
  const bannerConfig = getBannerConfig(store);
  const attachedCustomerId = resolveAttachedCustomerId(store);
  const customerFilesUrl = attachedCustomerId
    ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/files/customers/${attachedCustomerId}/files`
    : "";
  const [logoFiles, setLogoFiles] = useState([]);
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);
  const [logoUploadFile, setLogoUploadFile] = useState(null);
  const [bannerUploadFiles, setBannerUploadFiles] = useState({});
  const [bannerPickerSlot, setBannerPickerSlot] = useState(null);
  const [logoAssetLoading, setLogoAssetLoading] = useState(false);
  const [logoAssetError, setLogoAssetError] = useState("");

  const loadLogoFiles = async () => {
    if (!customerFilesUrl) {
      setLogoFiles([]);
      setLogoAssetError("No customer is attached to this PromoStore. Attach a customer in Overview to use customer files.");
      return;
    }
    setLogoAssetLoading(true);
    setLogoAssetError("");
    try {
      const res = await apiClient.get(customerFilesUrl, {
        params: { includeMeta: true },
      });
      setLogoFiles(res.data.files || []);
    } catch (error) {
      console.error("Error loading customer files", error);
      setLogoAssetError("Unable to load customer files.");
    } finally {
      setLogoAssetLoading(false);
    }
  };

  useEffect(() => {
    if (designTab === "branding") loadLogoFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designTab, customerFilesUrl]);

  const assignLogoUrl = (url) => {
    onStoreChange("ShopLogoUrl", url || "");
    onStoreChange("isSynced", false);
  };

  const assignBannerUrl = (slotKey, url) => {
    const nextValue = url || "";
    if (slotKey === "mainBannerUrl") {
      onStoreChange("HomepageBannerUrl", nextValue);
    }
    onStoreChange("TemplateConfig", setBannerConfig(store, slotKey, nextValue));
    onStoreChange("isSynced", false);
  };

  const handleLogoUpload = async () => {
    if (!customerFilesUrl || !logoUploadFile) {
      if (!customerFilesUrl) {
        setLogoAssetError("No customer is attached to this PromoStore. Attach a customer in Overview before uploading files.");
      }
      return;
    }
    setLogoAssetLoading(true);
    setLogoAssetError("");
    try {
      const formData = new FormData();
      formData.append("file", logoUploadFile);
      formData.append("folder", "branding");
      const res = await apiClient.post(customerFilesUrl, formData);
      const file = res.data.file;
      assignLogoUrl(resolveCustomerFileUrl(file));
      setLogoUploadFile(null);
      await loadLogoFiles();
    } catch (error) {
      console.error("Error uploading customer logo", error);
      setLogoAssetError("Unable to upload and assign logo.");
    } finally {
      setLogoAssetLoading(false);
    }
  };


  const handleBannerUpload = async (slotKey) => {
    const uploadFile = bannerUploadFiles[slotKey];
    if (!customerFilesUrl || !uploadFile) {
      if (!customerFilesUrl) {
        setLogoAssetError("No customer is attached to this PromoStore. Attach a customer in Overview before uploading files.");
      }
      return;
    }
    setLogoAssetLoading(true);
    setLogoAssetError("");
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("folder", "banners");
      const res = await apiClient.post(customerFilesUrl, formData);
      const file = res.data.file;
      assignBannerUrl(slotKey, resolveCustomerFileUrl(file));
      setBannerUploadFiles((prev) => ({ ...prev, [slotKey]: null }));
      await loadLogoFiles();
    } catch (error) {
      console.error("Error uploading customer banner", error);
      setLogoAssetError("Unable to upload and assign banner.");
    } finally {
      setLogoAssetLoading(false);
    }
  };
  const [aiStatus, setAiStatus] = useState("idle");
  const [aiMeta, setAiMeta] = useState({ requestId: "", stage: "", message: "" });

  const [promoSubtype, setPromoSubtype] = useState(
    get(store, "TemplateConfig.designSpec.businessSubCategory", store?.StoreType || "company-store")
  );

  const [generatePrompt, setGeneratePrompt] = useState(get(store, "TemplateConfig.designSpec.prompt", ""));
  const [generateFocus, setGenerateFocus] = useState(get(store, "TemplateConfig.designSpec.vibe", "general"));
  const [selectedPages, setSelectedPages] = useState(
    get(store, "TemplateConfig.designSpec.selectedPages", ["home"])
  );
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  // ✅ Add-on modal state
  const [addonModalKey, setAddonModalKey] = useState(null);

  const aiDraftUrl = get(store, "TemplateConfig.draftUrl", "");
  const aiDraftScreenshotUrl = get(store, "TemplateConfig.draftScreenshotUrl", "");

  const typeLabel = storeTypeLabel(store?.StoreType || "company-store");

  // ✅ derive siteKey from store.path (matches Node sync logic)
  const siteKey = useMemo(() => {
    return (store?.path || "/").replace(/^\/+|\/+$/g, "") || "root";
  }, [store?.path]);

  const menuConfig = useMemo(() => ensurePromoStoreMenuConfig(store?.TemplateConfig), [store?.TemplateConfig]);
  const menuSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const persistMenu = (nextMenu) => {
    onStoreChange("TemplateConfig", setPromoStoreMenuConfig(store?.TemplateConfig, nextMenu));
    onStoreChange("isSynced", false);
  };
  const addMenuItem = (item) => {
    const duplicate = menuConfig.items.some((existing) =>
      item.type === "category"
        ? existing.type === "category" && String(existing.categoryId) === String(item.categoryId)
        : item.type !== "custom-link" && existing.type === item.type
    );
    if (duplicate) return;
    persistMenu({ ...menuConfig, items: [...menuConfig.items, { id: createPromoStoreMenuItemId(), ...item }] });
  };
  const updateMenuItem = (id, patch) => persistMenu({
    ...menuConfig,
    items: menuConfig.items.map((item) => item.id === id ? { ...item, ...patch } : item),
  });
  const removeMenuItem = (id) => persistMenu({ ...menuConfig, items: menuConfig.items.filter((item) => item.id !== id) });
  const reorderMenuItems = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = menuConfig.items.findIndex((item) => item.id === active.id);
    const to = menuConfig.items.findIndex((item) => item.id === over.id);
    persistMenu({ ...menuConfig, items: arrayMove(menuConfig.items, from, to) });
  };
  const isSynced = store?.isSynced ?? store?.IsSynced ?? true;

  const activeAddon = configurableAddonDefinitions.find((a) => a.key === addonModalKey);

  const modalSettings = useMemo(() => {
    if (!addonModalKey) return {};
    return getAddonSettings(store?.TemplateConfig, addonModalKey, configurableAddonDefinitions);
  }, [addonModalKey, store?.TemplateConfig, configurableAddonDefinitions]);

  const canGenerate = useMemo(() => {
    const promptOk = (generatePrompt || "").trim().length >= 10;
    const pagesOk = Array.isArray(selectedPages) && selectedPages.length >= 1;
    return promptOk && pagesOk && !aiLoading;
  }, [generatePrompt, selectedPages, aiLoading]);

  const handleTemplateSelect = (tpl) => {
    onStoreChange("TemplateConfig", {
      ...(store.TemplateConfig || {}),
      id: tpl.id,
      name: tpl.name,
      label: tpl.label,
    });
    onStoreChange("isSynced", false);

    // ✅ if Custom Elite turns OFF while on Pages tab, bounce back safely
    if (tpl.id !== "custom-elite" && designTab === "pages") {
      setDesignTab("templates");
    }
  };

  const togglePage = (pageName) => {
    setSelectedPages((prev) => {
      const has = prev.includes(pageName);
      const next = has ? prev.filter((p) => p !== pageName) : [...prev, pageName];
      if (!next.length) return ["home"];

      onStoreChange("TemplateConfig", {
        ...(store.TemplateConfig || {}),
        designSpec: {
          ...(store.TemplateConfig?.designSpec || {}),
          selectedPages: next,
        },
      });
      onStoreChange("isSynced", false);

      return next;
    });
  };

  const persistAiInputsToTemplateConfig = () => {
    onStoreChange("TemplateConfig", {
      ...(store.TemplateConfig || {}),
      source: "businessbloc-ai",
      designSpec: {
        ...(store.TemplateConfig?.designSpec || {}),
        prompt: generatePrompt,
        vibe: generateFocus,
        selectedPages,
        businessSubCategory: promoSubtype,
      },
    });
    onStoreChange("isSynced", false);
  };

  // ✅ Add-on modal open/close
  const openAddonModal = (key) => {
    if (!configurableAddonDefinitions.some((addon) => addon.key === key)) return;
    setAddonModalKey(key);
  };
  const closeAddonModal = () => setAddonModalKey(null);

  const persistAddonSetting = (addonKey, partial) => {
    const nextCfg = normalizePromoStorePaymentGatewayState(
      setAddonSettings(store?.TemplateConfig, addonKey, partial, configurableAddonDefinitions),
    );

    // Update payload preview (optional)
    const preview = buildWpAddonSettingsPayload(nextCfg);
    nextCfg.addons.wpPayloadPreview = preview;

    onStoreChange("TemplateConfig", nextCfg);
    onStoreChange("isSynced", false);
  };

  const handleGenerateClick = async () => {
    setAiError("");
    setAiStatus("saving");
    setAiMeta({
      requestId: "",
      stage: "saving_inputs",
      message: "Saving your prompt and selections…",
    });

    persistAiInputsToTemplateConfig();

    if (typeof onGenerateAiDesign !== "function") {
      setAiStatus("error");
      setAiError("AI generation isn’t connected yet (Node route not wired).");
      return;
    }

    try {
      setAiLoading(true);
      setAiStatus("contacting");
      setAiMeta((m) => ({ ...m, stage: "calling_backend", message: "Contacting generator…" }));

      const result = await onGenerateAiDesign({
        prompt: generatePrompt,
        focus: generateFocus,
        selectedPages,
        businessSubCategory: promoSubtype,
      });

      const requestId = result?.requestId || "";
      const stage = result?.stage || "done";

      setAiStatus("done");
      setAiMeta({ requestId, stage, message: "Draft generated successfully!" });
    } catch (err) {
      console.error(err);

      const payload = parseAxiosOrFetchError(err);
      const friendly = friendlyAiError(payload);

      setAiStatus("error");
      setAiMeta({
        requestId: payload?.requestId || "",
        stage: payload?.stage || "",
        message: "",
      });

      setAiError(
        `${friendly.title}${friendly.body ? `\n${friendly.body}` : ""}${friendly.meta ? `\n${friendly.meta}` : ""}`
      );
    } finally {
      setAiLoading(false);
    }
  };

  // ✅ if parent doesn't supply onInitiateSync, still show a useful debug
  const handleInitiateSync = async () => {
    if (typeof onInitiateSync !== "function") return;

    const wpPayload = buildWpAddonSettingsPayload(store?.TemplateConfig);

    // ✅ IMPORTANT: include TemplateConfig so Node can read user-addresses.settings.locationSets
    return onInitiateSync({
      TemplateConfig: store?.TemplateConfig,
      ...buildPromoStoreBrandingSyncPayload(store),
      wpAddonsPayload: wpPayload,
    });
  };

  // --------------------------------------------
  // ✅ Pages (Custom Elite) state + loader
  // --------------------------------------------
  const [wpPages, setWpPages] = useState([]);
  const [wpPagesLoading, setWpPagesLoading] = useState(false);
  const [wpPagesError, setWpPagesError] = useState("");

  const loadWpPages = async () => {
    setWpPagesError("");
  
    if (typeof onGetWpPages !== "function") {
      setWpPagesError("Pages fetch isn’t wired yet (missing onGetWpPages prop).");
      return;
    }
  
    try {
      setWpPagesLoading(true);
      const result = await onGetWpPages();
      const pagesRaw = Array.isArray(result?.pages) ? result.pages : Array.isArray(result) ? result : [];
      setWpPages(pagesRaw.map((p) => normalizeWpPage(p)));
    } catch (e) {
      console.error(e);
      setWpPagesError("Failed to load WordPress pages.");
    } finally {
      setWpPagesLoading(false);
    }
  };
  
  const refreshWpPages = async () => {
    setWpPagesError("");
  
    if (typeof onRefreshWpPages !== "function") {
      setWpPagesError("Pages refresh isn’t wired yet (missing onRefreshWpPages prop).");
      return;
    }
  
    try {
      setWpPagesLoading(true);
      const result = await onRefreshWpPages();
      const pagesRaw = Array.isArray(result?.pages) ? result.pages : Array.isArray(result) ? result : [];
      setWpPages(pagesRaw.map((p) => normalizeWpPage(p)));
    } catch (e) {
      console.error(e);
      setWpPagesError("Failed to refresh WordPress pages.");
    } finally {
      setWpPagesLoading(false);
    }
  };
  

  // Auto-load when the Pages tab opens (only for Custom Elite)
  useEffect(() => {
    if (designTab === "pages" && isCustomElite && wpPages.length === 0 && !wpPagesLoading) {
      loadWpPages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designTab, isCustomElite]);

  // Keep tab safe if Custom Elite is turned off
  useEffect(() => {
    if (!isCustomElite && designTab === "pages") {
      setDesignTab("templates");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomElite]);

  return (
    <div className="design-section order-details-section">
      {/* ✅ Out of sync banner */}
      {!isSynced && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid #ffd7d7",
            background: "linear-gradient(135deg, rgba(195,23,29,0.07) 0%, rgba(0,0,0,0.02) 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800 }}>This store is out of sync with WordPress</Typography>
            <Typography variant="body2" sx={{ color: "#555" }}>
              You’ve changed design settings locally. Click “Initiate Sync” to push updates to the site.
            </Typography>
          </Box>

          {typeof onInitiateSync === "function" && (
            <Button
              variant="contained"
              onClick={handleInitiateSync}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                backgroundColor: "#c3171d",
                "&:hover": { backgroundColor: "#a11217" },
              }}
            >
              Initiate Sync
            </Button>
          )}
        </Box>
      )}

      {/* Design header + tabs */}
      <Box sx={{ mb: 3 }}>
      <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            mb: 1.5,
            flexWrap: "wrap", // keeps it nice on small screens
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
            Design Settings
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              fontWeight: 700,
              letterSpacing: 0.2,
              border: "1px solid rgba(0,0,0,0.12)",
              background:
                "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(245,245,245,1) 100%)",
              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
              color: "rgba(0,0,0,0.78)",
              whiteSpace: "nowrap",
            }}
          >
            <Box component="span" sx={{ opacity: 0.7, fontWeight: 600 }}>
              Store type:
            </Box>
            <Box component="span" sx={{ fontWeight: 800 }}>
              {typeLabel}
            </Box>
          </Typography>
        </Box>


        <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
          Control how this PromoStore looks and behaves. Branding, layout templates, and add-ons will evolve differently
          for Company, Pop-up, and Program stores over time.
        </Typography>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant={designTab === "branding" ? "contained" : "outlined"}
            size="small"
            sx={{
              textTransform: "none",
              borderColor: "#333",
              fontFamily: "Nunito",            
              backgroundColor: designTab === "branding" ? "#c3171d" : undefined,
              color: designTab === "branding" ? "#fff" : "#333",
              "&:hover": designTab === "branding" ? { backgroundColor: "#a11217" } : undefined,
            }}
            onClick={() => setDesignTab("branding")}
          >
            Branding
          </Button>

          <Button
            variant={designTab === "templates" ? "contained" : "outlined"}
            size="small"
            sx={{
              textTransform: "none",
              borderColor: "#333",
              fontFamily: "Nunito",
              backgroundColor: designTab === "templates" ? "#c3171d" : undefined,
              color: designTab === "templates" ? "#fff" : "#333",
              "&:hover": designTab === "templates" ? { backgroundColor: "#a11217" } : undefined,
            }}
            onClick={() => setDesignTab("templates")}
          >
            Templates
          </Button>

          {/* ✅ Pages tab only when Custom Elite is selected */}
          {isCustomElite && (
            <Button
              variant={designTab === "pages" ? "contained" : "outlined"}
              size="small"
              sx={{
                textTransform: "none",
                borderColor: "#333",
                fontFamily: "Nunito",
                backgroundColor: designTab === "pages" ? "#c3171d" : undefined,
                color: designTab === "pages" ? "#fff" : "#333",
                "&:hover": designTab === "pages" ? { backgroundColor: "#a11217" } : undefined,
              }}
              onClick={() => setDesignTab("pages")}
            >
              Pages
            </Button>
          )}

          <Button
            variant={designTab === "menus" ? "contained" : "outlined"}
            size="small"
            sx={{ textTransform: "none", borderColor: "#333", fontFamily: "Nunito", backgroundColor: designTab === "menus" ? "#c3171d" : undefined, color: designTab === "menus" ? "#fff" : "#333" }}
            onClick={() => setDesignTab("menus")}
          >
            Menus
          </Button>

          <Button
            variant={designTab === "addons" ? "contained" : "outlined"}
            size="small"
            sx={{
              textTransform: "none",
              borderColor: "#333",
              fontFamily: "Nunito",
              backgroundColor: designTab === "addons" ? "#c3171d" : undefined,
              color: designTab === "addons" ? "#fff" : "#333",
              "&:hover": designTab === "addons" ? { backgroundColor: "#a11217" } : undefined,
            }}
            onClick={() => setDesignTab("addons")}
          >
            Add-ons
          </Button>
        </Box>
      </Box>

      {/* TAB: Branding (✅ Logo preview only; Files controls logo) */}
      {designTab === "branding" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Store Logo
          </Typography>

          <Typography variant="body2" sx={{ color: "#666" }}>
            Files manages logo assets. Branding assigns the active logo used for sync.
          </Typography>

          <div
            className="image-selector__preview"
            style={{
              display: "inline-block",
              border: "2px dashed #ccc",
              padding: 8,
              borderRadius: 4,
              textAlign: "center",
              maxWidth: 220,
              cursor: "default",
              opacity: 0.95,
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Store Logo Preview"
                style={{ maxWidth: 200, height: "auto", borderRadius: 4 }}
              />
            ) : (
              <div
                style={{
                  width: 200,
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  color: "#999",
                }}
              >
                No logo uploaded yet
              </div>
            )}

            <div style={{ marginTop: 6, color: "#777", fontSize: 12 }}>Assigned in Branding</div>
          </div>

          {logoAssetError && (
            <Typography variant="body2" color="error">{logoAssetError}</Typography>
          )}

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" onClick={() => { setLogoPickerOpen(true); loadLogoFiles(); }}>
              Choose from Files
            </Button>
            <Button variant="outlined" component="label">
              Upload New
              <input hidden type="file" accept="image/*" onChange={(e) => setLogoUploadFile(e.target.files?.[0] || null)} />
            </Button>
            <Button variant="outlined" color="warning" onClick={() => assignLogoUrl("")}>
              Remove
            </Button>
          </Box>

          {logoUploadFile && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2">Selected: {logoUploadFile.name}</Typography>
              <Button variant="contained" disabled={logoAssetLoading} onClick={handleLogoUpload}>
                Upload and assign
              </Button>
            </Box>
          )}

          <Accordion disableGutters sx={{ maxWidth: 860 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Advanced logo URL</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                size="small"
                label="ShopLogoUrl"
                value={logoUrl}
                onChange={(e) => assignLogoUrl(e.target.value)}
                placeholder="Paste a Customer Files URL"
              />
            </AccordionDetails>
          </Accordion>

          <Dialog open={logoPickerOpen} onClose={() => setLogoPickerOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Choose logo from Customer Files</DialogTitle>
            <DialogContent dividers>
              {logoAssetLoading ? (
                <Typography>Loading files…</Typography>
              ) : logoFiles.length ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 1.5 }}>
                  {logoFiles.map((file) => {
                    const fileUrl = resolveCustomerFileUrl(file);
                    return (
                    <Button
                      key={file.id}
                      variant="outlined"
                      sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 120 }}
                      onClick={() => { assignLogoUrl(fileUrl); setLogoPickerOpen(false); }}
                    >
                      {String(file.mimeType || "").startsWith("image/") ? (
                        <Box component="img" src={fileUrl} alt={file.fileName} sx={{ maxWidth: "100%", maxHeight: 70, objectFit: "contain" }} />
                      ) : (
                        <ImageIcon />
                      )}
                      <Typography variant="caption" sx={{ wordBreak: "break-word" }}>{file.fileName}</Typography>
                    </Button>
                    );
                  })}
                </Box>
              ) : (
                <Typography>No customer files found. Upload a logo first.</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setLogoPickerOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={Boolean(bannerPickerSlot)} onClose={() => setBannerPickerSlot(null)} maxWidth="sm" fullWidth>
            <DialogTitle>Choose {bannerPickerSlot?.label || "banner"} from Customer Files</DialogTitle>
            <DialogContent dividers>
              {logoAssetLoading ? (
                <Typography>Loading files…</Typography>
              ) : logoFiles.length ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 1.5 }}>
                  {logoFiles.map((file) => {
                    const fileUrl = resolveCustomerFileUrl(file);
                    return (
                    <Button
                      key={file.id}
                      variant="outlined"
                      sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 120 }}
                      onClick={() => { assignBannerUrl(bannerPickerSlot.key, fileUrl); setBannerPickerSlot(null); }}
                    >
                      {String(file.mimeType || "").startsWith("image/") ? (
                        <Box component="img" src={fileUrl} alt={file.fileName} sx={{ maxWidth: "100%", maxHeight: 70, objectFit: "contain" }} />
                      ) : (
                        <ImageIcon />
                      )}
                      <Typography variant="caption" sx={{ wordBreak: "break-word" }}>{file.fileName}</Typography>
                    </Button>
                    );
                  })}
                </Box>
              ) : (
                <Typography>No customer files found. Upload a banner first.</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setBannerPickerSlot(null)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* BANNERS */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Banners
            </Typography>

            <Typography variant="body2" sx={{ color: "#666", mt: 0.5, mb: 1.25 }}>
              Files stores banner assets. Branding assigns which image is used for each banner slot.
            </Typography>

            {[
              { key: "mainBannerUrl", label: "Main banner", helper: "Uses the existing HomepageBannerUrl field." },
              { key: "cartBannerUrl", label: "Cart banner", helper: "Stored in TemplateConfig.banners.cartBannerUrl." },
              { key: "myAccountBannerUrl", label: "My Account banner", helper: "Stored in TemplateConfig.banners.myAccountBannerUrl." },
              { key: "productPageBannerUrl", label: "Product page banner", helper: "Stored in TemplateConfig.banners.productPageBannerUrl." },
            ].map((slot, index) => {
              const value = bannerConfig[slot.key] || "";
              return (
                <Accordion key={slot.key} defaultExpanded={index === 0} disableGutters sx={{ maxWidth: 860 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography sx={{ fontWeight: 700 }}>{slot.label}</Typography>
                      <Typography variant="caption" sx={{ color: "#777" }}>
                        {value || "No image URL set"}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                      <Button variant="outlined" onClick={() => { setBannerPickerSlot(slot); loadLogoFiles(); }}>
                        Choose from Files
                      </Button>
                      <Button variant="outlined" component="label">
                        Upload New
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={(e) => setBannerUploadFiles((prev) => ({ ...prev, [slot.key]: e.target.files?.[0] || null }))}
                        />
                      </Button>
                      <Button variant="outlined" color="warning" onClick={() => assignBannerUrl(slot.key, "")}>
                        Remove
                      </Button>
                    </Box>

                    {bannerUploadFiles[slot.key] && (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
                        <Typography variant="body2">Selected: {bannerUploadFiles[slot.key].name}</Typography>
                        <Button variant="contained" disabled={logoAssetLoading} onClick={() => handleBannerUpload(slot.key)}>
                          Upload and assign
                        </Button>
                      </Box>
                    )}

                    <Box
                      sx={{
                        border: "1px solid #e6e6e6",
                        borderRadius: 2,
                        overflow: "hidden",
                        background: "#fff",
                      }}
                    >
                      {value ? (
                        <Box
                          component="img"
                          src={value}
                          alt={`${slot.label} preview`}
                          sx={{
                            width: "100%",
                            maxHeight: 260,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 140,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#999",
                            fontSize: 13,
                            background: "linear-gradient(135deg, #f9f9f9 0%, #f1f1f1 60%, #ffffff 100%)",
                          }}
                        >
                          No banner uploaded yet
                        </Box>
                      )}
                    </Box>

                    <Accordion disableGutters sx={{ mt: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 700 }}>Advanced URL</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TextField
                          fullWidth
                          size="small"
                          label={`${slot.label} URL`}
                          value={value}
                          onChange={(e) => assignBannerUrl(slot.key, e.target.value)}
                          placeholder="Paste a Customer Files URL"
                          helperText={slot.helper}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>

        </Box>
      )}

      {/* TAB: Templates */}
      {designTab === "templates" && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Templates
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
            Pick a starting layout for this PromoStore.
          </Typography>
          {addonCatalogError ? <Typography color="error" sx={{ mb: 2 }}>{addonCatalogError}</Typography> : null}


          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
              {TEMPLATE_PRESETS.map((tpl) => {
                const isSelected = selectedTemplateId === tpl.id;
                const thumbnailUrl = TEMPLATE_THUMBNAILS[tpl.id] || null;

                return (
                  <Box
                    key={tpl.id}
                    sx={{
                      borderRadius: 2,
                      border: isSelected ? "2px solid #c3171d" : "1px solid #ddd",
                      p: 2,
                      backgroundColor: isSelected ? "rgba(195,23,29,0.04)" : "#fff",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, display: "flex", alignItems: "center" }}>
                      {tpl.label}
                      {isSelected && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            padding: "2px 6px",
                            borderRadius: 999,
                            backgroundColor: "#c3171d",
                            color: "#fff",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Selected
                        </span>
                      )}
                    </Typography>

                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {tpl.description}
                    </Typography>

                    {thumbnailUrl ? (
                      <Box
                        component="img"
                        src={thumbnailUrl}
                        alt={`${tpl.label} template preview`}
                        sx={{
                          mt: 1,
                          mb: 1,
                          height: 420,
                          width: "100%",
                          objectFit: "cover",
                          borderRadius: 1,
                          border: "1px solid #ddd",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          mt: 1,
                          mb: 1,
                          height: 80,
                          borderRadius: 1,
                          border: "1px dashed #ccc",
                          background: "linear-gradient(135deg, #f9f9f9 0%, #f1f1f1 60%, #ffffff 100%)",
                        }}
                      />
                    )}

                    <Button
                      variant={isSelected ? "contained" : "outlined"}
                      size="small"
                      sx={{
                        textTransform: "none",
                        alignSelf: "flex-start",
                        mt: 1,
                        backgroundColor: isSelected ? "#c3171d" : undefined,
                        "&:hover": isSelected ? { backgroundColor: "#a11217" } : undefined,
                      }}
                      onClick={() => handleTemplateSelect(tpl)}
                    >
                      {isSelected ? "Selected" : "Use this template"}
                    </Button>

                    {/* ✅ Helpful hint when Custom Elite is active */}
                    {tpl.id === "custom-elite" && isSelected && (
                      <Typography variant="caption" sx={{ color: "#666", mt: 1 }}>
                        Custom Elite unlocks a <b>Pages</b> tab (view/edit shortcuts).
                      </Typography>
                    )}
                  </Box>
                );
              })}
          </Box>
        </Box>
      )}

      {/* TAB: Pages (✅ Only when Custom Elite is selected) */}
      {designTab === "pages" && isCustomElite && (
        <Box sx={{ mt: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Pages (Custom Elite)
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Quick view/edit shortcuts for this store’s WordPress pages.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => window.open(buildWpBase(store), "_blank")}
                sx={{ fontFamily: "Nunito", color: "#333", borderColor: "#333", textTransform: "none", fontWeight: 700 }}
                startIcon={<LaunchIcon />}
              >
                View site
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={refreshWpPages}
                disabled={wpPagesLoading}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  backgroundColor: "#c3171d",
                  "&:hover": { backgroundColor: "#a11217" },
                }}
              >
                {wpPagesLoading ? "Loading…" : "Refresh pages"}
              </Button>
            </Box>
          </Box>

          {wpPagesError && (
            <Box sx={{ mb: 2, p: 1.25, borderRadius: 2, border: "1px solid #ffd7d7", background: "#fff5f5" }}>
              <Typography variant="body2" sx={{ color: "#b00020", fontWeight: 700 }}>
                {wpPagesError}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              border: "1px solid #eee",
              borderRadius: 2,
              overflow: "hidden",
              background: "#fff",
              width: "100%",
              maxWidth: "100%"
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" },
                p: 1.25,
                background: "#fafafa",
                borderBottom: "1px solid #eee",
                gap: 1,
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Page</Typography>
              <Typography sx={{ fontFamily: "Nunito", color: "#333", borderColor: "#333", fontWeight: 800, fontSize: 13, display: { xs: "none", sm: "block" } }}>
                View
              </Typography>
              <Typography sx={{ fontFamily: "Nunito", color: "#333", borderColor: "#333", fontWeight: 800, fontSize: 13, display: { xs: "none", sm: "block" } }}>
                Edit
              </Typography>
            </Box>

            {!wpPages || wpPages.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: "#777" }}>
                  No pages loaded yet.
                </Typography>
                <Typography variant="caption" sx={{ color: "#888" }}>
                  This will populate once the WordPress <code>/elitecore/v1/pages</code> route is wired and{" "}
                  <code>onFetchWpPages</code> is connected in the parent.
                </Typography>
              </Box>
            ) : (
              wpPages.map((p) => {
                const viewUrl = buildPageViewUrl(store, p);
                const editUrl = buildPageEditUrl(store, p);

                return (
                  <Box
                    key={p.id || p.slug}
                    sx={{
                      display: "grid",
                      fontFamily: "Nunito", 
                      color: "#333",
                      borderColor: "#333",
                      gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" },
                      p: 1.25,
                      borderBottom: "1px solid #f1f1f1",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>{p.title || "Untitled"}</Typography>
                      <Typography variant="caption" sx={{ color: "#777" }}>
                        ID: {p.id || "—"} • Slug: {p.slug || "—"}
                      </Typography>

                      {/* Mobile-friendly actions */}
                      <Box sx={{ display: { xs: "flex", sm: "none" }, gap: 1, mt: 1, flexWrap: "wrap" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => window.open(viewUrl, "_blank")}
                          sx={{ fontFamily: "Nunito", color: "#333", borderColor: "#333", textTransform: "none", fontWeight: 700 }}
                          startIcon={<LaunchIcon />}
                        >
                          View
                        </Button>

                        {editUrl ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => window.open(editUrl, "_blank")}
                            sx={{
                              fontFamily: "Nunito", 
                              color: "#333", 
                              borderColor: "#333", 
                              textTransform: "none",
                              fontWeight: 800,
                              backgroundColor: "#222",
                              "&:hover": { backgroundColor: "#111" },
                            }}
                          >
                            Edit (Elementor)
                          </Button>
                        ) : (
                          <Typography variant="caption" sx={{ color: "#999", alignSelf: "center" }}>
                            No edit link
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Desktop actions */}
                    <Box sx={{ display: { xs: "none", sm: "block" } }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => window.open(viewUrl, "_blank")}
                        sx={{ color: "#333 !important", borderColor: "#333 !important", textTransform: "none", fontWeight: 700 }}
                        startIcon={<LaunchIcon />}
                      >
                        View
                      </Button>
                    </Box>

                    <Box sx={{ display: { xs: "none", sm: "block" } }}>
                      {editUrl ? (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => window.open(editUrl, "_blank")}
                          sx={{
                            textTransform: "none",
                            fontWeight: 800,
                            backgroundColor: "#222",
                            "&:hover": { backgroundColor: "#111" },
                          }}
                        >
                          Edit (Elementor)
                        </Button>
                      ) : (
                        <Typography variant="caption" sx={{ color: "#999" }}>
                          No edit link
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      )}

      {designTab === "menus" && (
        <Box sx={{ mt: 1, mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Menus</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Build the navigation customers see on your PromoStore.</Typography>
          <Box sx={{ border: "1px solid #e1e5ea", borderRadius: 2, p: 2, mb: 2, background: "#fff" }}>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Display Locations</Typography>
            <FormControlLabel control={<Switch checked={menuConfig.locations.header} onChange={(event) => persistMenu({ ...menuConfig, locations: { ...menuConfig.locations, header: event.target.checked } })} />} label="Header" />
            <FormControlLabel control={<Switch checked={menuConfig.locations.footer} onChange={(event) => persistMenu({ ...menuConfig, locations: { ...menuConfig.locations, footer: event.target.checked } })} />} label="Footer" />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(260px, 0.8fr) minmax(340px, 1.2fr)" }, gap: 2 }}>
            <Box sx={{ border: "1px solid #e1e5ea", borderRadius: 2, p: 2, background: "#fff" }}>
              <Typography variant="overline" sx={{ fontWeight: 800 }}>Available Items</Typography>
              <Typography sx={{ fontWeight: 800, mt: 1, mb: 0.5 }}>Store</Typography>
              {PROMO_STORE_MENU_BUILT_INS.map((entry) => {
                const added = menuConfig.items.some((item) => item.type === entry.type);
                return <Button key={entry.type} fullWidth disabled={added} endIcon={<AddIcon />} onClick={() => addMenuItem(entry)} sx={{ justifyContent: "space-between", textTransform: "none" }}>{entry.label}</Button>;
              })}
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Categories</Typography>
              {categories.length ? categories.map((category) => {
                const added = menuConfig.items.some((item) => item.type === "category" && String(item.categoryId) === String(category.id));
                return <Button key={category.id} fullWidth disabled={added} endIcon={<AddIcon />} onClick={() => addMenuItem({ type: "category", categoryId: category.id, label: category.name })} sx={{ justifyContent: "space-between", textTransform: "none" }}>{category.name}</Button>;
              }) : <Typography variant="body2" color="text.secondary">No PromoStore categories yet.</Typography>}
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Custom Link</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => addMenuItem({ type: "custom-link", label: "Custom Link", url: "" })} sx={{ textTransform: "none" }}>Add Custom Link</Button>
            </Box>
            <Box sx={{ border: "1px solid #e1e5ea", borderRadius: 2, p: 2, background: "#f8fafc" }}>
              <Typography variant="overline" sx={{ fontWeight: 800 }}>Menu Structure</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Drag items to reorder. Expand an item to edit its navigation label.</Typography>
              {menuConfig.items.length ? (
                <DndContext sensors={menuSensors} collisionDetection={closestCenter} onDragEnd={reorderMenuItems}>
                  <SortableContext items={menuConfig.items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                    {menuConfig.items.map((item) => (
                      <SortableMenuItem key={item.id} item={item} category={categories.find((category) => String(category.id) === String(item.categoryId))} onChange={(patch) => updateMenuItem(item.id, patch)} onRemove={() => removeMenuItem(item.id)} />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>No menu items yet. Add an item from the left.</Typography>}
            </Box>
          </Box>
        </Box>
      )}

      {/* TAB: Add-ons */}
      {designTab === "addons" && (
        <Box sx={{ mt: 1 }}>
          <PromoStoreAddonConnection storeId={store?.id} />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
            <Button variant="outlined" onClick={() => openAddonModal("user-addresses")}>Location address definitions</Button>
            <Button variant="outlined" onClick={() => openAddonModal("payment-gateway")}>Stripe payment description</Button>
          </Box>

          {/* ✅ One modal that swaps content by add-on */}
          <Dialog open={!!addonModalKey} onClose={closeAddonModal} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800 }}>
              Customize Add-on{activeAddon?.name ? `: ${activeAddon.name}` : ""}
            </DialogTitle>

            <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {addonModalKey === "payment-gateway" && (
                <>
                  <Typography variant="body2">Card and quote availability is managed in Overview. Native quote settings are in Connected WordPress add-ons.</Typography>
                  <Box sx={{ display: "grid", gap: 1 }}>
                    <Typography variant="subtitle2">Stripe Payment Description</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={modalSettings?.stripe?.useCompanyDefault !== false}
                          onChange={(event) => persistAddonSetting("payment-gateway", {
                            stripe: {
                              ...(modalSettings.stripe || {}),
                              useCompanyDefault: event.target.checked,
                            },
                          })}
                        />
                      }
                      label="Use company default"
                    />
                    <TextField
                      label="Store-specific Stripe Payment Description"
                      value={modalSettings?.stripe?.paymentDescription || ""}
                      disabled={modalSettings?.stripe?.useCompanyDefault !== false}
                      onChange={(event) => persistAddonSetting("payment-gateway", {
                        stripe: {
                          ...(modalSettings.stripe || {}),
                          useCompanyDefault: false,
                          paymentDescription: event.target.value,
                        },
                      })}
                      helperText={'Available variables: {promoStoreName} {orderNumber} {wooOrderId} {customerName} {amount} {currency}. Inherited stores use the company value from Financial Configuration.'}
                      fullWidth
                    />
                  </Box>
                </>
              )}

              {/* USER ADDRESSES */}
              {addonModalKey === "user-addresses" && (
                <>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    Controls how address selection behaves for logged-in users.
                    <br />
                    Location sets save to:{" "}
                    <code>TemplateConfig.addons.settings["user-addresses"].locationSets</code>
                    <br />
                    Current siteKey: <b>{siteKey}</b>
                  </Typography>

                  <Divider />

                  <Alert severity="info">Configure checkout selector, locking and overrides in the connected User Addresses settings.</Alert>

                  <Divider />

                  <LocationSetEditor
                    siteKey={siteKey}
                    value={modalSettings.locationSets || {}}
                    onChange={(nextLocationSets) => {
                      persistAddonSetting("user-addresses", { locationSets: nextLocationSets });
                    }}
                  />
                </>
              )}

            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button onClick={closeAddonModal} sx={{ textTransform: "none", fontWeight: 700 }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

        </Box>
      )}
    </div>
  );
};

export default InstanceWebStoreDesign;
